import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { UNNAMED_AMENDMENT_QUOTE } from 'common/components/ScheduleAmendmentLock/scheduleAmendmentLock.constants'

export type ScheduleAmendmentLock = {
  isLocked: boolean
  quoteId: string | undefined
  /** The quote's own name, or a stand-in when we couldn't read the quote. */
  quoteName: string
  /**
   * False until we have a name - the lookup is still running, or it failed.
   * Either way there is nothing worth linking to, so callers show the stand-in
   * as plain text.
   */
  isQuoteNameKnown: boolean
}

/**
 * Reads a schedule's `lockedByQuoteId` and looks up the quote behind it, so the
 * UI can say which quote is holding the schedule rather than just that
 * something is.
 *
 * The lookup can legitimately come back empty - a user without quote read
 * permission still needs to be told the schedule is locked - so the name falls
 * back to a stand-in and callers drop the link.
 */
export const useScheduleAmendmentLock = (
  lockedByQuoteId: string | undefined
): ScheduleAmendmentLock => {
  const quoteQuery = useQuery(
    dashboard20240730Client.getQuote,
    { id: lockedByQuoteId ?? '' },
    {
      enabled: Boolean(lockedByQuoteId),
      select: quote => quote?.title || quote?.quoteNumber
    }
  )

  return {
    isLocked: Boolean(lockedByQuoteId),
    quoteId: lockedByQuoteId,
    quoteName: quoteQuery.data || UNNAMED_AMENDMENT_QUOTE,
    isQuoteNameKnown: Boolean(quoteQuery.data)
  }
}
